import React, { useMemo, useState } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, Icon, Text } from '@betterment/samba-react';
import { HR, Loader, Modal } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { BreakdownByYearContributionsProps, IraContributionYearBreakdownProps } from '../data';
import { useIraContributionsByYearQuery } from '../hooks';

const FlexBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1.6rem;
  margin-bottom: 1.6rem;
`;

const CollapsableContributionByYearWrapper = styled(Box)`
  && {
    cursor: pointer;
  }
`;

const CollapsableContributionByYear: React.FC<React.PropsWithChildren<IraContributionYearBreakdownProps>> = ({
  breakdown,
}) => {
  const [isCollapsedSection, setIsCollapsedSection] = useState(false);
  return (
    <>
      <HR />
      <CollapsableContributionByYearWrapper onClick={() => setIsCollapsedSection(!isCollapsedSection)}>
        <FlexBox>
          <Box css={{ display: 'flex', flexDirection: 'row' }}>
            <Box css={{ height: 'space.300', marginRight: 'space.100' }}>
              {isCollapsedSection ? (
                <Icon
                  name="CaretDown"
                  size="small"
                  mode="functional"
                  label={`Collapse ${breakdown.taxYear.year} Contributions`}
                />
              ) : (
                <Icon
                  name="CaretRight"
                  size="small"
                  mode="functional"
                  label={`Expand ${breakdown.taxYear.year} Contributions`}
                />
              )}
            </Box>
            <Text>{breakdown.taxYear.year}</Text>
          </Box>
          <Text>{formatMoney(breakdown.result.amount)}</Text>
        </FlexBox>
      </CollapsableContributionByYearWrapper>
      {isCollapsedSection &&
        breakdown.result.lineItems.map(lineItem => (
          <FlexBox key={lineItem.id}>
            <Text css={{ paddingLeft: 'space.400' }} variant="text50" color="text.weak">
              {lineItem.name}
            </Text>
            <Text variant="text50" color="text.weak">
              {formatMoney(lineItem.amount)}
            </Text>
          </FlexBox>
        ))}
    </>
  );
};

const BreakdownByYearContributions: React.FC<React.PropsWithChildren<BreakdownByYearContributionsProps>> = ({
  closeHandler,
}) => {
  const { isLoading, iraContributionByYear } = useIraContributionsByYearQuery();

  const totalIraContribution = useMemo(
    () => iraContributionByYear?.reduce((acc, curr) => acc + curr.result.amount.cents, 0) || 0,
    [iraContributionByYear]
  );

  return (
    <Modal closeHandler={closeHandler} contentWidth={['100%', '640px']} label="IRA deposit breakdown">
      <Modal.Slot name="heading">IRA deposit breakdown</Modal.Slot>
      <Modal.Slot name="content">
        <Box>
          {isLoading && <Loader pb="9" isLoading={true} />}
          {!isLoading && (
            <>
              {iraContributionByYear?.map(breakdown => (
                <CollapsableContributionByYear key={breakdown.result.id} breakdown={breakdown} />
              ))}
              <HR />
              <FlexBox>
                <Text css={{ fontWeight: 600 }}>Total deposits all time</Text>
                <Text css={{ fontWeight: 600 }}>{formatMoney(toMoney(totalIraContribution))}</Text>
              </FlexBox>
              <HR />
            </>
          )}
        </Box>
      </Modal.Slot>
    </Modal>
  );
};

export default BreakdownByYearContributions;
